import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { setCanonical, setMeta } from '@/utils/seo';
import { BreadcrumbSchema, SpeakableSchema, ArticleSchema } from '@/components/SEOSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuidePage() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/guides/${slug}`).then(r => {
      setGuide(r.data);
      setCanonical(`/guides/${slug}`);
      setMeta(r.data.meta_title, r.data.meta_description);
    }).catch(() => setGuide(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="ls-container py-20 text-center"><i className="fa-solid fa-spinner fa-spin text-2xl text-[var(--ls-navy)]"></i></div>;
  if (!guide) return <div className="ls-container py-20 text-center"><h1 className="text-2xl font-bold">Guide not found</h1></div>;

  const renderContent = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-bold mt-6 mb-3 text-[var(--ls-navy)]">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-bold mt-4 mb-2 text-[var(--ls-navy)]">{line.replace('### ', '')}</h3>;
      if (line.startsWith('- ')) return <li key={i} className="ml-6 mb-1 text-[var(--ls-text)] list-disc">{line.replace('- ', '')}</li>;
      if (line.match(/^\d+\. /)) return <li key={i} className="ml-6 mb-1 text-[var(--ls-text)] list-decimal">{line.replace(/^\d+\. /, '')}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="mb-3 text-[var(--ls-text)] leading-relaxed">{line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').split(/<strong>(.*?)<\/strong>/).map((part, j) => j % 2 === 1 ? <strong key={j} className="text-[var(--ls-navy)]">{part}</strong> : part)}</p>;
    });
  };

  return (
    <div data-testid="guide-page">
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Guides' }, { name: guide.title }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <ArticleSchema title={guide.title} description={guide.meta_description} url={`/guides/${slug}`} datePublished={guide.created_at} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <nav className="text-sm text-[var(--ls-text-light)] mb-4">
            <Link to="/" className="hover:text-[var(--ls-gold)]">Home</Link> / <span className="text-[var(--ls-text-light)]">Guides</span> / <span className="text-[var(--ls-navy)] font-medium">{guide.title}</span>
          </nav>
          <div className="inline-block mb-3 px-3 py-1 rounded-full bg-[var(--ls-gold)]/20 text-[var(--ls-gold-dark)] text-xs font-medium uppercase">{guide.category}</div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>{guide.title}</h1>
          <p className="speakable-intro text-lg max-w-2xl" style={{ color: '#4A5568' }}>{guide.excerpt}</p>
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container max-w-4xl">
          <div className="ls-card prose-content mb-8">
            {renderContent(guide.content)}
          </div>

          <div className="ls-card bg-[var(--ls-bg)] mb-8">
            <h3 className="font-bold text-[var(--ls-navy)] mb-3">Explore Related Guides</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <Link to="/guides/special-power-of-attorney-philippines" className="text-sm text-[var(--ls-gold-dark)] hover:underline"><i className="fa-solid fa-file-lines mr-2"></i>Special Power of Attorney Guide</Link>
              <Link to="/guides/guardian-mandate-filipino-children" className="text-sm text-[var(--ls-gold-dark)] hover:underline"><i className="fa-solid fa-file-lines mr-2"></i>Guardian Mandate Guide</Link>
              <Link to="/guides/estate-planning-for-ofw" className="text-sm text-[var(--ls-gold-dark)] hover:underline"><i className="fa-solid fa-file-lines mr-2"></i>Estate Planning for OFW</Link>
              <Link to="/guides/how-to-sell-property-philippines-while-abroad" className="text-sm text-[var(--ls-gold-dark)] hover:underline"><i className="fa-solid fa-file-lines mr-2"></i>Sell Property While Abroad</Link>
              <Link to="/guides/philippine-inheritance-law" className="text-sm text-[var(--ls-gold-dark)] hover:underline"><i className="fa-solid fa-file-lines mr-2"></i>Philippine Inheritance Law</Link>
              <Link to="/guides/apostille-requirements-ofw" className="text-sm text-[var(--ls-gold-dark)] hover:underline"><i className="fa-solid fa-file-lines mr-2"></i>Apostille Requirements</Link>
            </div>
          </div>

          <div className="ls-card border-l-4 border-amber-400 bg-amber-50 text-sm">
            <p className="text-amber-800"><i className="fa-solid fa-triangle-exclamation mr-2"></i><strong>Legal Disclaimer:</strong> This guide is for informational purposes only and does not constitute legal advice. For specific legal matters, please consult a licensed attorney. Visit <a href="https://legasecure.com" className="underline font-medium">legasecure.com</a> for professional estate planning tools.</p>
          </div>
        </div>
      </section>

      <section className="bg-[var(--ls-navy)] py-12">
        <div className="ls-container text-center">
          <h2 className="text-2xl font-bold text-white mb-3 font-['Crimson_Pro']">Your family deserves this peace of mind.</h2>
          <p className="text-white/70 mb-6 max-w-lg mx-auto">Start for free — it takes less than 5 minutes. No credit card, no commitment. Just clarity for the people who matter most.</p>
          <a href="https://legasecure.com" className="ls-btn-gold text-lg px-8 py-3">Get Started for Free <i className="fa-solid fa-arrow-right ml-2"></i></a>
        </div>
      </section>
    </div>
  );
}
